import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { usersService } from '../services/firestoreService';
import { activityLogService } from '../services/activityLogService';
import captchaService from '../services/captchaService';

const UserContext = createContext();

/**
 * Hook to access user context
 * @returns {Object} User context with user data and auth methods
 * @throws {Error} If used outside UserProvider
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * UserProvider component that manages authentication state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} UserProvider component
 */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Handle offline errors gracefully by setting user with basic info
   * @param {Object} firebaseUser - Firebase user object
   */
  const handleOfflineUser = useCallback((firebaseUser) => {
    console.warn('⚠️ App is offline. User data will be synced when connection is restored.');
    setUser({
      id: firebaseUser.uid,
      type: 'student', // Default assumption
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
      email: firebaseUser.email || '',
      active: true,
      firebaseUser: firebaseUser
    });
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setError(null);
      if (firebaseUser) {
        // User is signed in, get their data from Firestore
        try {
          const userData = await usersService.getUser(firebaseUser.uid);
          if (userData) {
            setUser({
              id: userData.id,
              type: userData.type,
              name: userData.name,
              email: userData.email,
              active: userData.active,
              firebaseUser: firebaseUser
            });
          } else {
            // User exists in Auth but not in Firestore - create profile
            try {
              console.log('📝 Creating user profile (user exists in Auth but not Firestore)...');
              await usersService.createUser(firebaseUser.uid, {
                name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
                email: firebaseUser.email || '',
                type: 'student', // Default to student
                active: true
              });
              const newUserData = await usersService.getUser(firebaseUser.uid);
              if (newUserData) {
                setUser({
                  id: newUserData.id,
                  type: newUserData.type,
                  name: newUserData.name,
                  email: newUserData.email,
                  active: newUserData.active,
                  firebaseUser: firebaseUser
                });
              }
            } catch (createError) {
              // Handle offline errors gracefully
              if (createError.code === 'unavailable' || createError.message?.includes('offline')) {
                handleOfflineUser(firebaseUser);
              } else {
                console.error('❌ Error creating user profile:', createError);
                console.error('Error code:', createError.code);
                console.error('Error message:', createError.message);
                
                // Check for permission errors
                if (createError.code === 'permission-denied' || createError.message?.includes('permission')) {
                  console.error('⚠️ PERMISSION ERROR - Check Firestore security rules!');
                  console.error('⚠️ User UID:', firebaseUser.uid);
                  console.error('⚠️ Make sure the rule allows: allow create: if isAuthenticated() && request.auth.uid == userId;');
                  setError('Permission denied. Please contact support.');
                } else {
                  setError('Failed to create user profile. Please try again.');
                }
                
                setUser(null);
              }
            }
          }
        } catch (error) {
          // Handle offline errors gracefully
          if (error.code === 'unavailable' || error.message?.includes('offline') || error.message?.includes('Failed to get document because the client is offline')) {
            handleOfflineUser(firebaseUser);
          } else {
            console.error('Error fetching user data:', error);
            setError('Failed to load user data. Please refresh the page.');
            setUser(null);
          }
        }
      } else {
        // User is signed out
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [handleOfflineUser]);

  /**
   * Sign up a new user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} name - User name
   * @param {string} userType - User type (default: 'student')
   * @returns {Promise<Object>} Created user object
   * @throws {Error} If signup fails
   */
  const signUp = async (email, password, name, userType = 'student') => {
    setError(null);
    try {
      // Verify CAPTCHA to prevent spam signups
      await captchaService.verifyAction('SIGNUP', email, {
        checkSuspicious: true
      });

      // Only allow student signups - admin users must be created in Firestore
      if (userType !== 'student') {
        throw new Error('Admin accounts cannot be created through signup. Please contact an administrator.');
      }

      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Create user profile in Firestore using Firebase Auth UID (always as student)
      await usersService.createUser(firebaseUser.uid, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        type: 'student',
        active: true
      });

      // Get the created user data
      const userData = await usersService.getUser(firebaseUser.uid);
      
      if (!userData) {
        throw new Error('Failed to retrieve user data after signup');
      }
      
      // Log signup activity
      await activityLogService.logActivity(firebaseUser.uid, activityLogService.ACTIVITY_TYPES.SIGNUP, {
        email,
        name,
        userType: 'student'
      });
      
      return {
        id: userData.id,
        type: userData.type,
        name: userData.name,
        email: userData.email,
        active: userData.active,
        firebaseUser: firebaseUser
      };
    } catch (error) {
      // Don't log expected user errors (they're handled in the UI)
      // Only log unexpected errors
      const expectedErrors = [
        'auth/email-already-in-use',
        'auth/invalid-email',
        'auth/weak-password',
        'auth/network-request-failed'
      ];
      
      if (!expectedErrors.includes(error.code)) {
        console.error('Unexpected error signing up:', error);
      }
      setError(error.message || 'Signup failed. Please try again.');
      throw error;
    }
  };

  /**
   * Sign in an existing user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<void>}
   * @throws {Error} If sign in fails
   */
  const signIn = async (email, password) => {
    setError(null);
    try {
      // Verify CAPTCHA to prevent brute force attacks
      await captchaService.verifyAction('LOGIN', email, {
        checkSuspicious: true
      });

      const userCredential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      
      // Log login activity
      await activityLogService.logActivity(userCredential.user.uid, activityLogService.ACTIVITY_TYPES.LOGIN, {
        email
      });
      
      // User state will be updated by onAuthStateChanged listener
    } catch (error) {
      console.error('Error signing in:', error);
      setError(error.message || 'Sign in failed. Please check your credentials.');
      throw error;
    }
  };

  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If reset fails
   */
  const resetPassword = async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
      return true;
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setError(error.message || 'Failed to send password reset email.');
      throw error;
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If sign out fails
   */
  const signOut = async () => {
    setError(null);
    try {
      console.log('🚪 Signing out...');
      const userId = user?.id || auth.currentUser?.uid;
      await firebaseSignOut(auth);
      
      // Log logout activity before clearing user state
      if (userId) {
        await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.LOGOUT, {});
      }
      
      setUser(null);
      console.log('✅ Signed out successfully');
      
      // Return success so caller can navigate
      return true;
    } catch (error) {
      console.error('❌ Error signing out:', error);
      setError('Failed to sign out. Please try again.');
      throw error;
    }
  };

  return (
    <UserContext.Provider value={{ user, signUp, signIn, resetPassword, signOut, loading, error }}>
      {children}
    </UserContext.Provider>
  );
};

